// Merlin family switcher for the portal top bar.
//
//   0 accessible  → renders nothing (the bar is unchanged for people who can't
//                   use a Merlin).
//   1 accessible  → a single plain "Merlin" tab (role chip), links straight in.
//   2+ accessible → ONE "Merlin ▾" control that opens a specialist switcher —
//                   the Merlins collapse into one branded entry instead of N
//                   look-alike tabs.
//
// Everything is derived from the Merlin registry (constants/merlins.ts); adding
// a new Merlin needs no change here.

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MerlinMark from './MerlinMark';
import {
  MERLIN_WORDMARK,
  MERLIN_FAMILY_ACCENT,
  activeMerlin,
  type MerlinSpecialist,
} from '../../constants/merlins';
import { NAV_DROPDOWN_TOP, navDropdownAnchor, type NavDropdownAnchor } from './navDropdownAnchor';
import styles from './MerlinNavSwitcher.module.css';

/** The dropdown's CSS width (MerlinNavSwitcher.module.css .menu), for edge clamping. */
const MENU_WIDTH = 320;

interface MerlinNavSwitcherProps {
  /** Merlins the user can reach (already scope-filtered — see accessibleMerlins). */
  merlins: MerlinSpecialist[];
}

const caret = (
  <svg
    className={styles.caret}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default function MerlinNavSwitcher({ merlins }: MerlinNavSwitcherProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Where the trigger sat when the menu opened: the dropdown is position:
  // fixed (pinned under the bar) so a sideways-scrolling dense tab row
  // cannot clip it. See navDropdownAnchor.ts.
  const [anchor, setAnchor] = useState<NavDropdownAnchor>(() => navDropdownAnchor(null, MENU_WIDTH));
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const active = activeMerlin(location.pathname, merlins);

  // Close on outside click / Escape while the menu is open.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (merlins.length === 0) return null;

  // Single access → a plain "Merlin" tab that links straight in.
  if (merlins.length === 1) {
    const m = merlins[0];
    const isActive = active?.id === m.id;
    return (
      <Link
        to={m.route}
        className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
        aria-label={m.fullName}
        title={m.fullName}
      >
        <MerlinMark accent={m.accent} size={28} />
        <span className={styles.word}>{MERLIN_WORDMARK}</span>
        <span className={styles.role} aria-hidden="true">{m.role}</span>
      </Link>
    );
  }

  // Multiple access → one branded switcher.
  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.tab} ${styles.trigger} ${active ? styles.tabActive : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          active
            ? `Merlin — ${active.role}, choose specialist`
            : 'Merlin — choose specialist'
        }
        onClick={() => {
          if (!open) setAnchor(navDropdownAnchor(triggerRef.current, MENU_WIDTH));
          setOpen((v) => !v);
        }}
      >
        <MerlinMark accent={active ? active.accent : MERLIN_FAMILY_ACCENT} size={28} />
        <span className={styles.word}>{MERLIN_WORDMARK}</span>
        {active ? (
          <span className={styles.role} aria-hidden="true">{active.role}</span>
        ) : (
          <span className={styles.count} aria-hidden="true">{merlins.length}</span>
        )}
        {caret}
      </button>

      {open && (
        <div className={styles.menu} role="menu" style={{ top: NAV_DROPDOWN_TOP, left: anchor.centerX }}>
          <div className={styles.menuHead}>Choose a Merlin</div>
          {merlins.map((m) => {
            const isActive = active?.id === m.id;
            return (
              <Link
                key={m.id}
                to={m.route}
                role="menuitem"
                className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
                aria-label={m.fullName}
                onClick={() => setOpen(false)}
              >
                <MerlinMark accent={m.accent} size={30} />
                <span className={styles.itemText}>
                  <span className={styles.itemName}>
                    {MERLIN_WORDMARK}{' '}
                    <span className={styles.chip} style={{ background: m.accent }}>
                      {m.role}
                    </span>
                  </span>
                  <span className={styles.itemBlurb}>{m.blurb}</span>
                  <span className={styles.itemScope}>{m.scopeNote}</span>
                </span>
                {isActive && (
                  <svg
                    className={styles.check}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
